/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { ButtonBase } from "@mui/material";
import { Form } from "antd";

import {ReactComponent as StarIcon} from '../../assets/icons/star.svg';
import {ReactComponent as AlbumIcon} from '../../assets/icons/album.svg';
import {ReactComponent as SongIcon} from '../../assets/icons/song.svg';
import {ReactComponent as EditOffIcon} from '../../assets/icons/uncheck-circle.svg';
import {ReactComponent as EditOnIcon} from '../../assets/icons/check-circle.svg';
import CustomSelect from "../CommonComponents/CustomSelect";
import CustomInput from "../CommonComponents/CustomInput";
import TagsInput from "../CommonComponents/TagsInput";
import CustomPhotoUpload from "../CommonComponents/CustomPhotoUpload";
import api from "../../api/apiClient";
import { endpoints } from "../../api/endpoints";
import { useEffect, useRef, useState } from "react";
import { AlbumType, ArtistType } from "../../types";
import { useStore } from "../../store";
import CustomPhotoUploadType2 from "../CommonComponents/CustomPhotoUploadType2";
import Loader from "../CommonComponents/Loader";

type NewSongInputFormProps =  {
    handleNewSongFinish: (value: any) => void
    loading: boolean
    fromSongRequest? : {
      songId: number,
      artistId: number,
      artistName: string,
      songName: string
    }
}

export default function NewSongInputForm ({loading, handleNewSongFinish, fromSongRequest}: NewSongInputFormProps) {

    const {store} = useStore();
    const text = store.text;
    const [artists, setArtists] = useState<{value: string,label: string}[]>([]);
    const [editWithChordEditor, setEditWithChordEditor] = useState(false);
    const [albums, setAlbums] = useState([]);
    const [composers, setComposers] = useState([]);

    const mapArtistIdAndName = useRef<{id: number, name: string}[]>([]);

    const handleArtistSearch = async (v: string = "") => {
      const result: any = await api.get(endpoints.artist.search + `&keyword=${encodeURIComponent(v)}&current=1&limit=5`)
      if(result.code === '200'){
        const formattedArr = result.data?.by?.map((a : ArtistType) => ({value: a?.artist_id, label: a?.name}));
        setArtists(formattedArr)
      }else{
        setArtists([])
      }
    }

    const handleAlbumSearch = async (v: string = "") => {
      const result: any = await api.get(endpoints.alblum.search + `&keyword=${v}&current=1&limit=5`);
      if(result.code === '200'){
        const formattedArr = result.data?.by?.map((a : AlbumType) => ({value: a?.id, label: a?.name}));
        setAlbums(formattedArr)
      }else{
        setAlbums([])
      }
    }

    const handleComposerSearch = async (v: string = "") => {
      const result: any = await api.get(endpoints.composer.search + `&keyword=${v}&current=1&limit=5`);
      if(result.code === '200'){
        const formattedArr = result.data?.by?.map((a : ArtistType) => ({value: a?.artist_id, label: a?.name}));
        setComposers(formattedArr)
      }else{
        setComposers([])
      }
    }

    const btnStyle = {
      color: 'white', 
      border: 'none', 
      borderRadius: '20px',
      padding: '10px 54px',
      margin: '10px 0',
      cursor: 'pointer',
      fontWeight: 'bold',
    };
    const [form] = Form.useForm();

    useEffect(()=> {
      handleArtistSearch(fromSongRequest?.artistName || "");
      handleAlbumSearch();
      handleComposerSearch();
      if(fromSongRequest){
        form.setFieldValue('songId',fromSongRequest.songId);
      }
    },[])
  
    return (
      <Form
        name="basic"
        form={form}
        labelCol={{
          span: 8,
        }}
        wrapperCol={{
          span: 16,
        }}
        style={{
          fontFamily: "inherit"
        }}
        initialValues={{
          remember: true,
        }}
        onFinish={handleNewSongFinish}
        autoComplete="off"
      >
        <Form.Item
          style={{display: 'none'}}
          name="songId"
        >
        </Form.Item>
        <Form.Item
          name="songPhoto"
          wrapperCol = {{span: 24}}
          style={{padding: "",color: "white",margin: "20px 0 35px 0"}}
        >
          <CustomPhotoUpload circle={false} text={text["Add Song Cover"]}/>
        </Form.Item>
        <Form.Item
          name="songName"
          wrapperCol = {{span: 24}}
          initialValue={fromSongRequest && fromSongRequest.songName}
          rules={[
            {
              required: true,
              message: `! Song Name is required`,
            },
            {
              validator: (_, value) =>
                value && value.length > 35
                  ? Promise.reject(new Error('Singer Name is too long'))
                  : Promise.resolve(),
            },
          ]}
          style={{padding: "",color: "white",margin: "20px 0 35px 0"}}
        >
          <CustomInput placeholder={text["Song Name"]}  prefix={<SongIcon />}  />
        </Form.Item>
        <Form.Item
          name="songNameMyanmar"
          wrapperCol = {{span: 24}}
          style={{color: "white",margin: "35px 0"}}
          rules={[
            {
              validator: (_, value) =>
                value && value.length > 35
                  ? Promise.reject(new Error('Song Name is too long'))
                  : Promise.resolve(),
            },
          ]}
        >
          <CustomInput placeholder={text["Song Name(Myanmar)"]}  prefix={<SongIcon />}  />
        </Form.Item>
        {/* <Form.Item
          name="songNameEnglish"
          wrapperCol = {{span: 24}}
          style={{color: "white",margin: "35px 0"}}
          rules={[
            {
              validator: (_, value) =>
                value && value.length > 35
                  ? Promise.reject(new Error('Song Name is too long'))
                  : Promise.resolve(),
            },
          ]}
        >
          <CustomInput placeholder={text["Song Name(English)"]}  prefix={<SongIcon />}  />
        </Form.Item> */}
        <Form.Item
          name="singerId"
          initialValue={fromSongRequest && fromSongRequest.artistId && [fromSongRequest.artistId]}
          wrapperCol = {{span: 24}}
          rules={[
            {
              required: true,
              message: `!At least one artist is required`,
            },
          ]}
          style={{color: "white",margin: "35px 0"}}
        >
          <CustomSelect
            size='large'
            showSearch
            filterOption={false}
            suffixIcon={<></>} 
            prefixIcon={<StarIcon />}
            placeholder={text.Singer}
            onSearch={handleArtistSearch}
            options={artists}
            mode="multiple"
            multiSelect={true}
            onChange={(v)=> {
              // console.log("v", v)
              let singerName = artists.filter((a : any) => a.value === v[v.length - 1])[0]?.label;
              mapArtistIdAndName.current.push({
                id: v[v.length - 1],
                name: singerName
              });

              const filteredArtists = Array.from(
                new Map(
                  mapArtistIdAndName.current
                    .filter(artist => v.includes(artist.id)) // Keep only matching IDs
                    .map(artist => [artist.id, artist]) // Convert to [id, object] pairs
                ).values() // Extract unique values
              );
              mapArtistIdAndName.current = filteredArtists;
              const joinedNames = filteredArtists.map(artist => artist.name).join("+");

              form.setFieldValue('singerName',joinedNames);
            }}
          />
        </Form.Item>
        <Form.Item
          style={{display: 'none'}}
          name="singerName"
        >
        </Form.Item>
        <Form.Item
          name="composerId"
          wrapperCol = {{span: 24}}
          style={{color: "white",margin: "35px 0"}}
        >
          <CustomSelect
            size='large'
            showSearch
            filterOption={false}
            suffixIcon={<></>} 
            prefixIcon={<StarIcon />}
            placeholder={text.Composer}
            onSearch={handleComposerSearch}
            options={composers}
          />
        </Form.Item>
        <Form.Item
          name="albumId"
          wrapperCol = {{span: 24}}
          style={{color: "white",margin: "35px 0"}}
        >
          <CustomSelect
            size='large'
            showSearch
            filterOption={false}
            suffixIcon={<></>} 
            prefixIcon={<AlbumIcon />}
            placeholder={text.Album}
            onSearch={handleAlbumSearch}
            options={albums}
          />
        </Form.Item>
        <div>
          <span style={{fontSize: "13px",color: "#5B5B5B",paddingBottom: "5px",display: "inline-block"}}>{"Remark ( Choose one or both )"}</span>
          <div style={{display: 'flex', alignItems: "center", flexDirection: "column", width: "100%"}}>
            <Form.Item
              name="lyricsImg"
              wrapperCol = {{span: 24}}
              style={{padding: "",color: "white",margin: "10px 0 0 0",width: "100%"}}
              css={css`
                .ant-upload-wrapper{
                  width: 100% !important;
                }
                .ant-upload-select{
                  width: 100% !important;
                }
            ` }
            >
              <CustomPhotoUploadType2 useCropper={false}  showPreview={true} displayText={false} outlineSideText={'Upload Lyrics Image'} alwaysDisplayUploadOutline={true} circle={false} style={{width: "100%",height: "60px",borderRadius: 0, margin: 0, backgroundColor: "transparent", border: "1px solid #333333"}}/>
            </Form.Item>
            <Form.Item
              name="editWithChordEditor"
              wrapperCol = {{span: 24}}
              style={{padding: "",color: "white",margin: "10px 0 0 0", width: "100%"}}
            >
              <div style={{display: 'flex', alignItems: 'center',backgroundColor: "transparent", border: "1px solid #333333",width: "100%",height: "60px"}}
                onClick={() => {
                  form.setFieldValue('editWithChordEditor',!editWithChordEditor);
                  setEditWithChordEditor(pre => !pre)
                }}
              >
                {
                  editWithChordEditor ?
                  <EditOnIcon style={{width: "30px", height: "30px", paddingRight: "20px",color: "white",paddingLeft: "10px"}}/>
                  :
                  <EditOffIcon style={{width: "30px", height: "30px", paddingRight: "20px",color: "white", paddingLeft: "10px"}}/>
                }
                <h3 style={{fontSize: "15px",margin: 0, color: editWithChordEditor? '#007BFF' : 'white'}}>Edit With Chord Editor</h3>
              </div>
            </Form.Item>
          </div>
        </div>
        <Form.Item
          name="tags"
          wrapperCol = {{span: 24}}
          style={{padding: "",color: "white",margin: "35px 0 0 0"}}
          // initialValue={['hello','hell']}
        >
          <span style={{fontSize: "13px",color: "#5B5B5B",paddingBottom: "10px",display: "inline-block"}}>{"Tag ( Optional )"}</span>
          <TagsInput/>
        </Form.Item>
        <div style={{display: "flex",justifyContent: "center"}}>
          <ButtonBase disabled={loading}  style={{...btnStyle,backgroundColor: '#007bff'}} type="submit">
            { loading ? <Loader loaderColor="black" btn/> : editWithChordEditor ? text.Next : text.Adding}
          </ButtonBase>
        </div>
      </Form>
    )
  }